import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AddDomainInput, EntraConfigInput, EntraConfigResponse, EntraTestResult, SsoHealthResponse, TenantDomainResponse } from '@orion/contracts';
import { api } from '../../../lib/api-client.js';

const SSO_ENTRA_KEY = ['admin-sso-entra'];
const SSO_DOMAINS_KEY = ['admin-sso-domains'];
const SSO_HEALTH_KEY = ['admin-sso-health'];

export function useEntraConfigQuery() {
  return useQuery({ queryKey: SSO_ENTRA_KEY, queryFn: () => api.get<EntraConfigResponse>('/admin/sso/entra') });
}

export function useUpdateEntraConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EntraConfigInput) => api.put<EntraConfigResponse>('/admin/sso/entra', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SSO_ENTRA_KEY }),
  });
}

export function useTestEntraConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<EntraTestResult>('/admin/sso/entra/test', {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SSO_ENTRA_KEY }),
  });
}

export function useSsoHealthQuery() {
  return useQuery({ queryKey: SSO_HEALTH_KEY, queryFn: () => api.get<SsoHealthResponse>('/admin/sso/health') });
}

export function useDomainsQuery() {
  return useQuery({ queryKey: SSO_DOMAINS_KEY, queryFn: () => api.get<TenantDomainResponse[]>('/admin/sso/domains') });
}

export function useAddDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddDomainInput) => api.post<TenantDomainResponse>('/admin/sso/domains', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SSO_DOMAINS_KEY }),
  });
}

export function useVerifyDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<TenantDomainResponse>(`/admin/sso/domains/${id}/verify`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SSO_DOMAINS_KEY }),
  });
}

export function useRemoveDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/admin/sso/domains/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SSO_DOMAINS_KEY }),
  });
}

export function useSetRequireSso() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (required: boolean) => api.patch<TenantDomainResponse[]>('/admin/sso/domains/require-sso', { required }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SSO_DOMAINS_KEY }),
  });
}
